import { Button } from '@systemeio/components';
import { Ticket01Icon } from '@systemeio/icons';
import { CurrencyEnum } from '@systemeio/types';
import { BadRequest, InternalError } from '@systemeio/util';
import { CouponForm } from 'modules/coupons/components/coupon-form';
import { DiscountTypeSelector } from 'modules/coupons/components/discount-type-selector';
import { CouponCreateInterface, CouponErrorsInterface, CouponInterface, DiscountTypeInterface } from 'modules/coupons/types/coupon-interface';
import { IconTile } from 'shared/components/icon-tile';
import { getAvailableCouponsData } from 'modules/funnels/funnel/components/step-configuration-tab/coupon-selector/utils/coupons-utils';
import React, { useState } from 'react';
import { FormSelect, Modal, toast } from 'shared/components';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import { FieldLabel } from 'shared/components/form/field-label';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { CouponDefaultErrorsInterface } from '../types/default-errors-interface';
type AttachCouponModalMode = 'select' | 'create';
const ATTACH_FORM_ID = 'attach-coupon-form';
interface AttachCouponModalProps {
  opened: boolean;
  onClose: () => void;
  currency: CurrencyEnum;
  coupons?: CouponInterface[];
  onAttachCoupon: (couponId: number) => Promise<void>;
  onCreateCoupon: (data: CouponCreateInterface) => Promise<number | undefined>;
  couponFieldErrors: CouponDefaultErrorsInterface;
  createFieldErrors: CouponErrorsInterface;
}
function AttachCouponModal({
  opened,
  onClose,
  currency,
  coupons,
  onAttachCoupon,
  onCreateCoupon,
  couponFieldErrors,
  createFieldErrors
}: AttachCouponModalProps) {
  const {
    t
  } = useLocoTranslation();
  const [mode, setMode] = useState<AttachCouponModalMode>('select');
  const [discountType, setDiscountType] = useState<DiscountTypeInterface['id']>('percent');
  const [couponId, setCouponId] = useState<number | undefined>(undefined);
  const [isFetching, setIsFetching] = useState(false);
  const availableCouponsData = getAvailableCouponsData(coupons, discountType);
  const resetState = () => {
    setMode('select');
    setDiscountType('percent');
    setCouponId(undefined);
    setIsFetching(false);
  };
  const handleChangeDiscountType = (value: DiscountTypeInterface['id']) => {
    setDiscountType(value);
    const isStillAvailable = getAvailableCouponsData(coupons, value)?.some(coupon => coupon.id === couponId);
    if (!isStillAvailable) setCouponId(undefined);
  };
  const handleAttach = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!couponId) return;
    setIsFetching(true);
    try {
      await onAttachCoupon(couponId);
    } catch (e) {
      // Field and common errors are surfaced through couponFieldErrors.
      if (!(e instanceof BadRequest)) notifyUnexpectedError(e);
    } finally {
      setIsFetching(false);
    }
  };

  // A coupon is created to be used, so it is attached to the offer right away.
  const handleCreate = async (data: CouponCreateInterface) => {
    setIsFetching(true);
    try {
      const createdCouponId = await onCreateCoupon(data);
      if (createdCouponId !== undefined) await onAttachCoupon(createdCouponId);
    } catch (e_0) {
      // Field errors are surfaced through createFieldErrors.
      if (!(e_0 instanceof BadRequest)) notifyUnexpectedError(e_0);
    } finally {
      setIsFetching(false);
    }
  };
  const notifyUnexpectedError = (e_1: unknown) => {
    toast.error(e_1 instanceof InternalError ? t('core.error.internal_error_message') : t('global.error'));
  };
  const isCreateMode = mode === 'create';
  return <Modal afterLeave={resetState} isFetching={isFetching} opened={opened} onClose={onClose} title={<div className="flex items-center gap-4">
          <IconTile size="m" tone="gray">
            <Ticket01Icon />
          </IconTile>
          <div className="flex flex-col">
            <span>
              {isCreateMode ? t('dashboard.funnel.configuration.offer.coupon_create-title') : t('dashboard.funnel.configuration.offer.coupon_add-title')}
            </span>
            <span className="text-text-tertiary text-sm font-normal">
              {isCreateMode ? t('dashboard.funnel.configuration.offer.coupon_create-description') : t('dashboard.funnel.configuration.offer.coupon_add-description')}
            </span>
          </div>
        </div>} footer={<>
          <Button className="tablet:flex-none flex-1" disabled={isFetching} onClick={onClose} size="md" type="button" variant="secondary">
            {t('global.cancel')}
          </Button>
          <Button className="tablet:flex-none flex-1" disabled={!isCreateMode && !couponId} form={isCreateMode ? CouponForm.formId : ATTACH_FORM_ID} isLoading={isFetching} size="md" type="submit" variant="primary">
            {t('global.add')}
          </Button>
        </>}>
      {isCreateMode ? <div className="flex flex-col gap-5">
          <CouponForm currency={currency} defaultValues={{
        discountType
      }} fieldErrors={createFieldErrors} onSubmit={handleCreate} />
          <div className="text-text-primary flex flex-wrap items-center gap-2 text-base">
            <span>{t('dashboard.funnel.configuration.offer.create_coupon_footer')}</span>
            <Button onClick={() => setMode('select')} type="button" variant="link">
              {t('dashboard.funnel.configuration.offer.create_coupon_footer_link')}
            </Button>
          </div>
        </div> : <form className="flex flex-col gap-5" id={ATTACH_FORM_ID} onSubmit={handleAttach}>
          <div className="flex flex-col gap-2">
            <FieldLabel label={t('dashboard.funnel.configuration.offer.coupon_discount_type')} required />
            <DiscountTypeSelector onChange={handleChangeDiscountType} value={discountType} />
          </div>
          <FormSelect data={availableCouponsData} error={couponFieldErrors.coupons} label={t('dashboard.funnel.configuration.offer.coupon')} onChange={setCouponId} required value={couponId} />
          <div className="text-text-primary flex flex-wrap items-center gap-2 text-base">
            <span>{t('dashboard.funnel.configuration.offer.add_coupon_footer')}</span>
            <Button onClick={() => setMode('create')} type="button" variant="link">
              {t('dashboard.funnel.configuration.offer.add_coupon_footer_link')}
            </Button>
          </div>
          <FieldErrorAndDescription error={couponFieldErrors.common} errorClassName="mt-2 text-center" />
        </form>}
    </Modal>;
}
export default AttachCouponModal;
export type { AttachCouponModalProps };